import { BookingStatus } from 'store/bookings/types';

/**
 * RES-1174. The two statuses a landlord rejection lands on, and the one label a tenant sees for
 * both of them.
 *
 * `MAP_STATUSES` already maps each to its own key -- "Denied Available" and "Denied Unavailable" --
 * but those name an internal enum variant rather than anything a tenant can act on, and the
 * distinction between them is not the tenant's to care about. Both resolve to one key here.
 *
 * ⛔ The old keys are deliberately left in place and `MAP_STATUSES` is deliberately not edited.
 * It has three renderers, and the third is the landing page carousel, which this ticket
 * intentionally does not surface rejections on -- editing the map would reach it.
 */
export const REJECTION_STATUSES: Array<BookingStatus> = [
  BookingStatus.DENIED_AVAILABLE,
  BookingStatus.DENIED_UNAVAILABLE,
];

/**
 * ⭐ Reuses the catalogue's existing `denied` key rather than minting a new one. It was already
 * present in all 19 catalogues and referenced nowhere in code -- a finished orphan -- and each
 * non-English value already gender-agrees with that locale's own sibling statuses, which a new
 * key would have had to re-derive. fr is `Refusée` (feminine, agreeing with `Acceptée`/`Annulée`)
 * where the `denied_available` it replaces is the masculine `Refusé`.
 *
 * Rejected alternative: a new `booking_not_approved` / "Not approved". It would have been a
 * FIFTH English word for one event -- the tenant's denial email says "Declined", this catalogue
 * says "Denied", its own prose says "denied", the admin says "Deny" -- and negations run about
 * twice as wide in de/hu/nl, in the narrowest component we have. (🐋 locale-expert, RES-1174.)
 */
export const REJECTED_STATUS_LABEL_ID = 'denied';

export const isRejectedStatus = (status?: BookingStatus): boolean => REJECTION_STATUSES.includes(status);

/**
 * Whether to render the landlord's explanation, keyed on STATUS and never on the presence of a
 * value.
 *
 * `resource_booking_statuses.reason` is one column shared with the cancel path, so a value on the
 * row does not by itself mean it is a rejection reason. A server-side guard restricts writes to
 * the two statuses above today; RES-1181 adds cancellation reasons and removes that guarantee,
 * at which point presence-keying would start captioning a cancellation as a rejection.
 */
export const shouldRenderRejectionReason = (status?: BookingStatus, reason?: string | null): boolean =>
  isRejectedStatus(status) && Boolean(reason);
